/**
 * Shared auth-session persistence helper (May 5, 2026)
 *
 * Single canonical writer for `localStorage['vhire_user']`. Used by:
 * - `app/auth/callback/page.tsx` (OAuth)
 * - `app/auth/verify/page.tsx` indirectly via `useAuth().verifyToken` (magic link)
 * - any future registration flow that mints a session
 *
 * Both auth paths previously wrote slightly different shapes:
 *   - OAuth callback wrote `{candidateId, email}` from the JWT, then enriched
 *     with `fullName` via a follow-up `/me/full` fetch (bug 7140e1a fix).
 *   - Magic-link verify wrote `{candidateId, email, fullName}` directly when
 *     the verify response included it.
 *
 * The apply-flow prefill reads `user.fullName` from the auth context. Any
 * code path that fails to populate fullName makes the form land empty.
 * This helper ensures every authenticated session has the same shape.
 *
 * Returns the (eventually) hydrated object so callers can use it inline if
 * they want to skip the localStorage round-trip.
 */

import { setAuthToken, candidateApi } from './api';

export interface PersistedUser {
  candidateId: string;
  email: string;
  fullName?: string;
}

interface PersistOpts {
  /** If the auth response already includes a name, pass it to skip the /me/full hop. */
  fullName?: string;
}

const STORAGE_KEY = 'vhire_user';

/**
 * Decode a JWT payload without verifying signature.
 * Returns null on any decode error.
 */
function decodeJwtPayload(jwt: string): { candidateId?: string; email?: string } | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/**
 * Persist an authenticated session.
 *
 * 1. Stores the JWT via setAuthToken.
 * 2. Decodes the JWT for candidateId + email and writes the minimal shape.
 * 3. If `fullName` was provided, writes it inline.
 * 4. Otherwise fires `/me/full` (best-effort) and rewrites `vhire_user`
 *    once the candidate's name is known. Caller doesn't need to await this.
 *
 * Returns the initial persisted shape (without fullName if not provided up
 * front; the enrichment runs async in the background).
 */
export function persistAuthSession(jwt: string, opts: PersistOpts = {}): PersistedUser | null {
  if (!jwt) return null;

  setAuthToken(jwt);

  const payload = decodeJwtPayload(jwt);
  if (!payload?.candidateId || !payload?.email) {
    // JWT shape unrecognised — nothing to persist beyond the raw token.
    return null;
  }

  const initial: PersistedUser = {
    candidateId: payload.candidateId,
    email: payload.email,
    fullName: opts.fullName?.trim() || undefined,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
  } catch {
    // Quota / storage disabled — non-fatal, auth context will still read
    // candidateId + email from the JWT itself on next mount via verifyToken.
  }

  // If we already have fullName, skip the enrichment fetch.
  if (initial.fullName) {
    return initial;
  }

  // Fire-and-forget enrichment. The apply page's own prefill effect also
  // calls getFullProfile, so missing this race is degraded-but-correct: the
  // form populates a beat later instead of immediately.
  candidateApi
    .getFullProfile()
    .then((res) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const c = (res?.data?.candidate || (res as any)?.candidate) as
        | { fullName?: string; name?: string; first_name?: string; last_name?: string; firstName?: string; lastName?: string }
        | undefined;
      if (!c) return;
      const fullName =
        c.fullName ||
        [c.first_name || c.firstName, c.last_name || c.lastName].filter(Boolean).join(' ').trim() ||
        c.name ||
        undefined;
      if (!fullName) return;
      const enriched: PersistedUser = { ...initial, fullName };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(enriched));
      } catch {
        // ignore
      }
    })
    .catch(() => {
      // Non-fatal; apply page falls back to its own prefill.
    });

  return initial;
}

/**
 * Read the currently-persisted user (if any). Useful for components mounted
 * before the auth context has hydrated from localStorage.
 */
export function readPersistedUser(): PersistedUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedUser;
  } catch {
    return null;
  }
}
